/**
 * QualityReconciliationLane — Sprint A.4
 *
 * Replaces AcoBucket in the FinalizationPage.
 * Shows ALL quality activity from this encounter.
 *
 * Three categories:
 *   ✓ Auto-captured (no input) — green checks
 *   ⏳ Staged orders — "will complete when result arrives"
 *   ⊘ Pending confirmation — action buttons + transcript snippet
 *
 * Design principle: provider makes at most ONE decision per item.
 * No modal. No interrupt. Just a clean list of decisions at finalization.
 */
import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ShieldCheck, Check, Clock, HelpCircle, RefreshCw, AlertCircle,
  ChevronDown, ChevronUp, Mic
} from 'lucide-react';
import { toast } from 'sonner';
import { BucketHeader } from './shared';

export function QualityReconciliationLane({ enc, outputs, encounterId, fetchEncounter, qualityOverview }) {
  const [chipState, setChipState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(null);
  const [showCompliant, setShowCompliant] = useState(false);

  const fetchChipState = () => {
    if (!encounterId) return;
    setLoading(true);
    // POST /refresh forces A&P re-scan so finalization always reflects current note
    api.post(`/encounters/${encounterId}/quality-chip-state/scan-note`)
      .catch(() => {})  // scan is best-effort
      .finally(() => {
        api.get(`/encounters/${encounterId}/quality-chip-state`)
          .then(r => setChipState(r.data))
          .catch(() => {})
          .finally(() => setLoading(false));
      });
  };

  useEffect(() => { fetchChipState(); }, [encounterId]); // eslint-disable-line

  const handleAction = async (chip, actionId) => {
    setExecuting(`${chip.measure_id}_${actionId}`);
    try {
      const signalId = chip.evidence_this_visit?.[0]?.signal_id || '';
      await api.post(`/encounters/${encounterId}/quality-chip-action`, {
        measure_id: chip.measure_id,
        action_id: actionId,
        signal_id: signalId,
      });
      const labels = {
        confirm_decline: `Decline documented — ${chip.short_label}`,
        rediscuss: 'Will re-evaluate at next visit',
      };
      if (actionId.startsWith('order_')) toast.success(`${chip.short_label} order staged`);
      else toast.success(labels[actionId] || 'Done');
      fetchChipState();
      fetchEncounter();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setExecuting(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <BucketHeader icon={ShieldCheck} title="Quality / ACO" />
        <div className="space-y-1.5">
          {[1,2,3].map(i => <div key={i} className="h-10 bg-muted/50 rounded-lg animate-pulse" />)}
        </div>
      </div>
    );
  }

  const chips = chipState?.chips || [];
  const pendingItems = chips.filter(c => c.status === 'pending_confirmation');
  const stagedItems = chips.filter(c => c.status === 'staged');
  const compliantItems = chips.filter(c => c.status === 'compliant');
  const dueItems = chips.filter(c => ['due', 'overdue'].includes(c.status));
  const summary = chipState?.summary;

  const totalDecisions = pendingItems.length;
  const isAllClear = totalDecisions === 0;
  const status = isAllClear ? 'complete' : 'pending';

  // Auto-captured this visit (compliant with evidence_this_visit)
  const autoCapturedThisVisit = compliantItems.filter(c => c.evidence_this_visit?.length > 0);

  return (
    <div className="space-y-4" data-testid="quality-reconciliation-lane">
      <div className="flex items-center gap-2">
        <BucketHeader icon={ShieldCheck} title="Quality / ACO"
          count={chips.length} status={status} />
        {summary && (
          <span className="text-[10px] text-muted-foreground ml-auto">
            {summary.compliant} compliant · {summary.due} due · {summary.pending} pending
          </span>
        )}
      </div>

      {/* ── Section 1: Pending Confirmation (physician decisions needed) ── */}
      {pendingItems.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            <HelpCircle className="w-3 h-3 text-violet-500" />
            Needs Your Decision
          </p>
          {pendingItems.map(chip => (
            <div key={chip.measure_id}
              className="rounded-xl border border-violet-200 bg-violet-50/20 p-3 space-y-2"
              data-testid={`quality-pending-${chip.measure_id}`}>
              <div className="flex items-center gap-2">
                <Badge className="text-[9px] bg-violet-50 text-violet-700 border-violet-200 gap-0.5">
                  <HelpCircle className="w-2.5 h-2.5" /> {chip.short_label}
                </Badge>
                <span className="text-xs text-muted-foreground flex-1">{chip.why_open}</span>
              </div>

              {/* Transcript snippet — context for decision */}
              {chip.evidence_this_visit?.[0]?.snippet && (
                <div className="rounded-lg bg-violet-100/50 border border-violet-200 px-3 py-2">
                  <p className="text-[9px] text-violet-600 font-semibold mb-0.5 flex items-center gap-1">
                    <Mic className="w-2.5 h-2.5" /> Patient said this visit:
                  </p>
                  <blockquote className="text-xs text-violet-800 italic line-clamp-3">
                    "{chip.evidence_this_visit[0].snippet}"
                  </blockquote>
                  <p className="text-[9px] text-violet-500 mt-0.5">
                    Confidence: {Math.round((chip.evidence_this_visit[0].confidence || 0) * 100)}%
                  </p>
                </div>
              )}

              {/* Action buttons — max 3 */}
              <div className="flex gap-1.5 flex-wrap">
                {(chip.actions_available || []).slice(0, 3).map(action => {
                  const key = `${chip.measure_id}_${action.id}`;
                  const isExec = executing === key;
                  return (
                    <Button key={action.id}
                      size="sm"
                      variant={action.style === 'default' ? 'default' : action.style === 'outline' ? 'outline' : 'ghost'}
                      className="h-7 text-[10px] px-3 gap-1"
                      onClick={() => handleAction(chip, action.id)}
                      disabled={!!executing}
                      data-testid={`quality-action-${action.id}`}>
                      {isExec ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      {action.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Section 2: Staged Orders ── */}
      {stagedItems.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            <Clock className="w-3 h-3 text-blue-500" />
            Staged — Awaiting Results
          </p>
          {stagedItems.map(chip => (
            <div key={chip.measure_id}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50/20"
              data-testid={`quality-staged-${chip.measure_id}`}>
              <Clock className="w-3.5 h-3.5 text-blue-500 shrink-0" />
              <span className="text-xs font-medium flex-1">{chip.short_label}</span>
              <span className="text-[10px] text-blue-600">Will auto-complete when result arrives</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Section 3: Auto-Captured This Visit ── */}
      {autoCapturedThisVisit.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            <Check className="w-3 h-3 text-emerald-500" />
            Captured This Visit
          </p>
          {autoCapturedThisVisit.map(chip => (
            <div key={chip.measure_id}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50/20"
              data-testid={`quality-captured-${chip.measure_id}`}>
              <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span className="text-xs font-medium flex-1">{chip.short_label}</span>
              {chip.evidence_this_visit?.[0] && (
                <span className="text-[9px] text-emerald-600">
                  {chip.evidence_this_visit[0].label || chip.evidence_this_visit[0].source?.replace(/_/g,' ')}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Section 4: Due / Open (not addressed this visit) ── */}
      {dueItems.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            <AlertCircle className="w-3 h-3 text-amber-500" />
            Open Gaps (not addressed this visit)
          </p>
          {dueItems.map(chip => (
            <div key={chip.measure_id}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50/10"
              data-testid={`quality-due-${chip.measure_id}`}>
              <AlertCircle className={`w-3.5 h-3.5 shrink-0 ${chip.status === 'overdue' ? 'text-red-500' : 'text-amber-500'}`} />
              <span className="text-xs font-medium flex-1">{chip.short_label}</span>
              <span className={`text-[9px] ${chip.status === 'overdue' ? 'text-red-600' : 'text-amber-600'}`}>
                {chip.status === 'overdue' ? 'Overdue' : 'Due'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Compliant — collapsed by default */}
      {compliantItems.length > 0 && (
        <div>
          <button
            onClick={() => setShowCompliant(c => !c)}
            className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors">
            {showCompliant ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {compliantItems.length} compliant measure{compliantItems.length !== 1 ? 's' : ''}
          </button>
          {showCompliant && (
            <div className="space-y-1 mt-1.5 pl-2">
              {compliantItems.map(chip => (
                <div key={chip.measure_id} className="flex items-center gap-2 text-[10px] text-muted-foreground py-0.5">
                  <Check className="w-3 h-3 text-emerald-400 shrink-0" />
                  {chip.short_label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {chips.length === 0 && (
        <div className="text-center py-8">
          <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">No quality measures for this patient type</p>
        </div>
      )}
    </div>
  );
}
